import type { BoardObjectV2 } from "@bezpart-board/shared";

type RemapOpts = {
  /** Скачать исходный ассет (уже с нужной auth). */
  fetchAsset: (assetId: string) => Promise<Response>;
  uploadBlob: (blob: Blob, filename: string) => Promise<{ id: string }>;
};

function collectAssetIds(objects: BoardObjectV2[]): string[] {
  const ids = new Set<string>();
  for (const obj of objects) {
    if (obj.type === "image" || obj.type === "audio") {
      const id = (obj as { assetId?: string }).assetId;
      if (typeof id === "string" && id) ids.add(id);
    } else if (obj.type === "pdf_document") {
      const pdf = obj as { sourceAssetId?: string; pageAssetIds?: string[] };
      if (typeof pdf.sourceAssetId === "string" && pdf.sourceAssetId) {
        ids.add(pdf.sourceAssetId);
      }
      for (const pageId of pdf.pageAssetIds || []) {
        if (typeof pageId === "string" && pageId) ids.add(pageId);
      }
    }
  }
  return [...ids];
}

function filenameFromResponse(res: Response, assetId: string): string {
  const cd = res.headers.get("content-disposition") || "";
  const match = /filename\*?=(?:UTF-8''|")?([^\";]+)/i.exec(cd);
  if (match?.[1]) {
    try {
      return decodeURIComponent(match[1].replace(/"/g, "").trim());
    } catch {
      return match[1].replace(/"/g, "").trim();
    }
  }
  const mime = (res.headers.get("content-type") || "").split(";")[0]?.trim() || "";
  if (mime.startsWith("image/")) return `${assetId}.${mime.slice(6) || "png"}`;
  if (mime === "application/pdf") return `${assetId}.pdf`;
  if (mime.startsWith("audio/")) return `${assetId}.${mime.slice(6) || "bin"}`;
  return `${assetId}.bin`;
}

/**
 * Перезаливает ассеты объектов на текущую доску и подменяет id.
 * Нужно при вставке с другой доски / вкладки.
 */
export async function remapPasteAssets(
  objects: BoardObjectV2[],
  opts: RemapOpts
): Promise<{ objects: BoardObjectV2[]; failed: number }> {
  const assetIds = collectAssetIds(objects);
  if (!assetIds.length) return { objects, failed: 0 };

  const map = new Map<string, string>();
  let failed = 0;

  for (const assetId of assetIds) {
    try {
      const res = await opts.fetchAsset(assetId);
      if (!res.ok) throw new Error(`asset ${assetId} HTTP ${res.status}`);
      const blob = await res.blob();
      const uploaded = await opts.uploadBlob(blob, filenameFromResponse(res, assetId));
      if (!uploaded?.id) throw new Error("upload without id");
      map.set(assetId, uploaded.id);
    } catch {
      failed += 1;
    }
  }

  if (!map.size && failed) return { objects, failed };

  const next = objects.map((obj) => {
    const clone = JSON.parse(JSON.stringify(obj)) as BoardObjectV2;
    if (clone.type === "image" || clone.type === "audio") {
      const media = clone as { assetId?: string };
      if (media.assetId && map.has(media.assetId)) {
        media.assetId = map.get(media.assetId)!;
      }
    } else if (clone.type === "pdf_document") {
      const pdf = clone as { sourceAssetId?: string; pageAssetIds?: string[] };
      if (pdf.sourceAssetId && map.has(pdf.sourceAssetId)) {
        pdf.sourceAssetId = map.get(pdf.sourceAssetId)!;
      }
      if (Array.isArray(pdf.pageAssetIds)) {
        pdf.pageAssetIds = pdf.pageAssetIds.map((id) =>
          typeof id === "string" && map.has(id) ? map.get(id)! : id
        );
      }
    }
    return clone;
  });

  return { objects: next, failed };
}
